import { loadConcepts } from '@kc/core/content/parsing'
import type { Library } from '@kc/core/content/library'
import type { RawFile } from '@kc/core/ports'

import type { ContentCache } from './cache'
import { asContentError, ContentError } from './errors'
import type { ContentBundle, ContentSource, LoadedContent } from './types'
import { parseBundle } from './validate'

export interface LoadOptions {
  readonly force?: boolean
}

interface ParsedBundle {
  readonly files: readonly RawFile[]
  readonly library: Library
}

/**
 * 把 content.json 里的 Markdown 映射成 RawFile[]，交给核心解析。
 * 客户端不自己解析 Markdown，也不自己数题目；数字一律来自 Library。
 */
export function parseContentBundle(bundle: ContentBundle): ParsedBundle {
  const files: RawFile[] = bundle.concepts.map((concept) => ({
    path: concept.path as RawFile['path'],
    text: concept.text,
  }))

  const loaded = loadConcepts(files)
  if (loaded.issues.length > 0) {
    const detail = loaded.issues
      .slice(0, 4)
      .map((issue) => `${issue.file}:${issue.line} ${issue.code} ${issue.message}`)
      .join('\n')
    const suffix = loaded.issues.length > 4 ? `\n…另有 ${loaded.issues.length - 4} 条` : ''
    throw new ContentError('invalid', `内容包解析失败：\n${detail}${suffix}`)
  }

  return { files, library: loaded.library }
}

async function readCache(cache: ContentCache, key: string) {
  try {
    return await cache.get(key)
  } catch {
    return null
  }
}

async function writeCache(cache: ContentCache, record: Parameters<ContentCache['put']>[0]): Promise<void> {
  try {
    await cache.put(record)
  } catch {
    // 缓存不可用时，本次内容仍然可读。
  }
}

/**
 * 一次内容加载：
 * 1. 先读 IndexedDB；有缓存就带着 sha 去问远端。
 * 2. 远端说没变 -> 用缓存；变了 -> 拉 blob / JSON，重新校验、解析、写缓存。
 * 3. 网络失败但有缓存 -> 返回缓存，offline=true，error 留给界面提示。
 * 4. 网络失败且无缓存 -> 抛错。
 */
export async function loadContent(
  source: ContentSource,
  cache: ContentCache,
  options: LoadOptions = {},
): Promise<LoadedContent> {
  const cached = await readCache(cache, source.key)
  const force = options.force ?? false

  try {
    const result = await source.load({ cached, force })

    const bundle = result.status === 'not-modified' ? cached?.bundle : result.bundle
    if (bundle === undefined) {
      throw new ContentError('invalid', '远端报告内容未变，但本地没有可用缓存。')
    }

    const { files, library } = parseContentBundle(parseBundle(bundle))
    const checkedAt = Date.now()
    const fetchedAt = result.status === 'not-modified' ? (cached?.fetchedAt ?? checkedAt) : checkedAt

    await writeCache(cache, {
      key: source.key,
      sha: result.sha,
      bundle,
      fetchedAt,
      checkedAt,
    })

    return {
      bundle,
      library,
      files,
      sha: result.sha,
      origin: result.status === 'updated' ? 'remote' : 'cache',
      fetchedAt,
      checkedAt,
      offline: false,
    }
  } catch (error) {
    const failure = asContentError(error)

    if (cached !== null) {
      try {
        const { files, library } = parseContentBundle(parseBundle(cached.bundle))
        return {
          bundle: cached.bundle,
          library,
          files,
          sha: cached.sha,
          origin: 'cache',
          fetchedAt: cached.fetchedAt,
          checkedAt: cached.checkedAt,
          offline: true,
          error: failure,
        }
      } catch (cacheError) {
        // 缓存本身坏了，优先把远端失败报出来，附一句缓存也不可用。
        const corrupt = asContentError(cacheError)
        throw new ContentError(failure.code, `${failure.message}\n本地缓存也无法解析：${corrupt.message}`)
      }
    }

    throw failure
  }
}
